import { FolderTreeOutput, ListSharesOutput } from "@zapier/bff";
import { RouterInputs, RouterOutputs, trpc } from "../trpc";

export type CreateAssetShareInput = RouterInputs["permission"]["createShare"];
export type CreateAssetShareOutput = RouterOutputs["permission"]["createShare"];

type Share = ListSharesOutput[number];

const hasSubjectId = (share: CreateAssetShareInput): share is Share =>
  "subjectId" in share;

function isSameShare(a: Share, b: Share): boolean {
  return (
    a.assetId === b.assetId &&
    a.assetType === b.assetType &&
    a.subjectId === b.subjectId &&
    a.subjectType === b.subjectType
  );
}

export function addOrUpdateShare(
  shares: Share[] | undefined,
  newShare: Share,
): Share[] {
  if (!shares) {
    return [newShare];
  }

  const copy = [...shares];
  const index = copy.findIndex((item) => isSameShare(item, newShare));

  if (index === -1) {
    // Value does not exist, add it
    copy.push(newShare);
  } else {
    // Value exists, update it
    copy[index] = newShare;
  }

  return copy;
}
function markFolderAsShared(
  cache: FolderTreeOutput | undefined,
  folderId: string,
): FolderTreeOutput | undefined {
  if (!cache) {
    return undefined;
  }

  const index = cache.tree.findIndex((item) => item.id === folderId);

  // Skip updating if there's no matching folder or if it's already marked as shared
  if (index === -1 || cache.tree[index].isShared) {
    return cache;
  }

  // Mark the folder as shared
  const nextTree = [...cache.tree];
  nextTree[index] = { ...nextTree[index], isShared: true };

  return { ...cache, tree: nextTree };
}

export function useCreateAssetShare() {
  const utils = trpc.useUtils();

  return trpc.permission.createShare.useMutation({
    onMutate(newShare) {
      const listSharesArguments = {
        assetId: newShare.assetId,
        assetType: newShare.assetType,
      };

      // Creating a share without a subjectId (e.g. creating a public link)
      // means we can't identify it later to update it, so we skip the optimistic update
      if (!hasSubjectId(newShare)) {
        return { listSharesArguments };
      }

      const previousShares =
        utils.permission.listShares.getData(listSharesArguments);
      const nextShares = addOrUpdateShare(previousShares, newShare);

      // Optimistically update the cache to include or update the share
      utils.permission.listShares.setData(
        listSharesArguments,
        () => nextShares,
      );

      let previousFolderTree: FolderTreeOutput | undefined;
      const hasPreviousShares = previousShares && previousShares.length > 0;

      // If sharing a folder, update the folder tree cache to mark the folder as shared
      if (newShare.assetType === "folder" && !hasPreviousShares) {
        previousFolderTree = utils.folder.folderTree.getData();

        utils.folder.folderTree.setData({}, (cache) =>
          markFolderAsShared(cache, newShare.assetId),
        );
      }

      return {
        listSharesArguments,
        previousFolderTree,
        previousShares,
      };
    },

    onError(_error, variables, context) {
      if (hasSubjectId(variables) && context?.listSharesArguments) {
        utils.permission.listShares.setData(
          context.listSharesArguments,
          context.previousShares,
        );
      }
      if (context?.previousFolderTree !== undefined) {
        utils.folder.folderTree.setData({}, context.previousFolderTree);
      }
    },

    async onSettled() {
      utils.permission.listShares.invalidate();
      utils.folder.folderTree.invalidate();
    },
  });
}

// `createShare` also updates existing shares. This is just an alias for clarity.
export const useUpdateAssetShare = useCreateAssetShare;
